"use client";

import NextImage, { type ImageProps as NextImageProps } from "next/image";
import NextLink, { type LinkProps as NextLinkProps } from "next/link";

import { ConsumableProductDetail } from "@/composite/ConsumableProductDetail";
import type {
	AllergenAndIngredients,
	ConsumableProductDetails,
	NutritionAndIngredients
} from "@/types/references";

import styles from "./styles.module.scss";

interface ConsumableProductDetailBlockProps extends ConsumableProductDetails {
	allergenIngredients: AllergenAndIngredients[];
	moreDetails?: NutritionAndIngredients;
	cta?: {
		icon_name: string;
		label: string;
	};
}

/**
 * Convert PascalCase or camelCase icon name to kebab-case
 * e.g. "AlertCircle" → "alert-circle"
 */
function toKebabCase(name: string): string {
	return name
		.replace(/([a-z0-9])([A-Z])/g, "$1-$2")
		.replace(/([A-Z])([A-Z][a-z])/g, "$1-$2")
		.toLowerCase();
}

/**
 * ConsumableProductDetailBlock component
 */
export function ConsumableProductDetailBlock(
	props: ConsumableProductDetailBlockProps
): JSX.Element {
	return (
		<div className={styles["consumable-product-detail"]}>
			<ConsumableProductDetail<NextLinkProps, NextImageProps>
				allergenIngredients={props.allergenIngredients.map(
					(allergenIngredient) => ({
						icon: {
							name: toKebabCase(
								allergenIngredient.icon.icon_name as string
							)
						},
						label: allergenIngredient.label
					})
				)}
				heading={{
					children: props.consumable_product_details_title,
					tag: props.consumable_product_details_heading_tag
				}}
				description={props.consumable_product_details_content}
				subheadingAbout={{
					children: props.consumable_product_details_subtitle,
					tag: props.consumable_product_details_subheading_tag
				}}
				subheadingDetails={{
					children: props.detail_content_subtitle,
					tag: props.detail_content_subtitle_heading_tag
				}}
				details={props.consumable_product_details_content}
				cta={{
					icon_name: toKebabCase(props.cta?.icon_name ?? ""),
					label: props.cta?.label ?? ""
				}}
				rteContent={props.moreDetails?.content}
				rteImage={{
					alt: props.moreDetails?.title ?? "",
					src: props.moreDetails?.image?.image ?? "",
					component: NextImage,
					height: 600,
					objectFit: "contain",
					width: 600
				}}
				rteCta={{
					href: props.moreDetails?.call_to_action?.link?.href ?? "",
					children: props.cta?.label ?? "",
					icon: {} as any,
					component: NextLink
				}}
			/>
		</div>
	);
}
